// Activates or approximates dark-mode rendering of an email for preview.
// - "dark"   : signal dark preference — activate the email's OWN dark CSS (Apple Mail class).
// - "forced" : approximate a force-invert client (Gmail/Outlook class) via double-invert.
// We never force-invert for "dark"; we never rely on authored dark CSS for "forced".

const COLOR_SCHEME_STYLE = '<style>:root{color-scheme:dark !important}</style>';

// Double-invert the light rendering: flip the whole document dark, then re-invert media
// so images keep their original pixels (reproducing white-image-band artifacts).
const FORCED_DARK_STYLE =
  '<style>html{background-color:#fff;color-scheme:light;filter:invert(1) hue-rotate(180deg)}'
  + 'img,picture,video,svg{filter:invert(1) hue-rotate(180deg)}</style>';

// `prefers-color-scheme` evaluates against the real OS, not our injected `color-scheme`.
// Swap the feature for a width feature that is a tautology or impossible for a rendered
// email viewport, to force a query on/off deterministically.
const ALWAYS_TRUE = 'min-width: 0px'; // every rendered viewport width is >= 0px
const ALWAYS_FALSE = 'min-width: 99999999px'; // no rendered email viewport is this wide

function forceScheme(html: string, dark: boolean, light: boolean): string {
  return html
    .replace(/prefers-color-scheme\s*:\s*dark/gi, dark ? ALWAYS_TRUE : ALWAYS_FALSE)
    .replace(/prefers-color-scheme\s*:\s*light/gi, light ? ALWAYS_TRUE : ALWAYS_FALSE);
}

// Inject a <style> without breaking document parsing. Never place a tag before a leading
// <!doctype>, which would push the doctype late and drop the iframe into quirks mode.
function injectStyle(html: string, styleTag: string): string {
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${styleTag}</head>`);
  }
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (m) => `${m}${styleTag}`);
  }
  if (/<body[^>]*>/i.test(html)) {
    return html.replace(/<body[^>]*>/i, (m) => `${styleTag}${m}`);
  }
  const doctype = html.match(/^\s*<!doctype[^>]*>/i);
  if (doctype) {
    return html.slice(0, doctype[0].length) + styleTag + html.slice(doctype[0].length);
  }
  return styleTag + html;
}

/** Signal dark preference: activate the email's own dark CSS (Apple Mail class). */
export function transformForDarkPreview(html: string): string {
  return injectStyle(forceScheme(html, true, false), COLOR_SCHEME_STYLE);
}

/** Approximate a force-invert client (Gmail/Outlook class): invert the light rendering. */
export function transformForForcedDarkPreview(html: string): string {
  return injectStyle(forceScheme(html, false, true), FORCED_DARK_STYLE);
}

export function themedSrcdoc(html: string, theme: 'light' | 'dark' | 'forced'): string {
  if (theme === 'dark') return transformForDarkPreview(html);
  if (theme === 'forced') return transformForForcedDarkPreview(html);
  return html;
}
